import i18next from 'i18next';
import logger from '@widgets/logger';

const resolveLocale = () => {
    const language = i18next.language;
    return language === 'UA' ? 'uk-UA' : language;
};

const format = (data, notation, style, options = {}) => {
    const formatter = new Intl.NumberFormat(resolveLocale(), { notation, style, ...options });
    return formatter.format(data);
};

const clean = value => value.replace(/\.$/, '').replace(/\s/g, ' ');

export function formatNumber(data, notation = 'standard') {
    try {
        const number = Number(data);
        return format(number, notation, 'decimal')?.replace(/\.$/, '')?.replace(/\s/g, ' ');
    } catch (e) {
        logger.error('formatNumber', data);
    }
}

export const isMissing = data => data === null || data === undefined;

export const formatOptionalNumber = data => (isMissing(data) ? '—' : formatNumber(data));

export const formatOptionalPercent = data => (isMissing(data) ? '—' : formatPercentNumber(data));

export function formatMoney(amount, currency) {
    try {
        return format(Number(amount), 'standard', 'currency', { currency });
    } catch (e) {
        return [formatNumber(amount), currency].filter(Boolean).join(' ');
    }
}

export function formatPercentNumber(data, notation = 'standard') {
    try {
        const number = Number(data);
        return format(number / 100, notation, 'percent');
    } catch (e) {
        logger.error('formatPercentNumber', data);
    }
}

export function formatFixedNumber(data, fractionDigits) {
    try {
        const number = Number(data);
        return clean(
            format(number, 'standard', 'decimal', {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }),
        );
    } catch (e) {
        logger.error('formatFixedNumber', data);
    }
}

const NUMBER_PART_TYPES = ['integer', 'group', 'decimal', 'fraction'];

const splitParts = (data, notation, style, options = {}) => {
    const number = style === 'percent' ? Number(data) / 100 : Number(data);
    const parts = new Intl.NumberFormat(resolveLocale(), { notation, style, ...options }).formatToParts(number);
    const first = parts.findIndex(part => NUMBER_PART_TYPES.includes(part.type));
    if (first === -1) {
        return { prefix: '', number: clean(parts.map(part => part.value).join('')), suffix: '' };
    }
    let last = first;
    for (let i = parts.length - 1; i >= first; i--) {
        if (NUMBER_PART_TYPES.includes(parts[i].type)) {
            last = i;
            break;
        }
    }
    const join = slice => slice.map(part => part.value).join('');
    return {
        prefix: clean(join(parts.slice(0, first))),
        number: clean(join(parts.slice(first, last + 1))),
        suffix: clean(join(parts.slice(last + 1))),
    };
};

export function formatNumberParts(data, notation = 'standard') {
    try {
        return splitParts(data, notation, 'decimal');
    } catch (e) {
        logger.error('formatNumberParts', data);
        return { prefix: '', number: formatNumber(data, notation) ?? '', suffix: '' };
    }
}

export function formatPercentNumberParts(data, notation = 'standard', fractionDigits) {
    try {
        const options =
            fractionDigits === undefined
                ? {}
                : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits };
        return splitParts(data, notation, 'percent', options);
    } catch (e) {
        logger.error('formatPercentNumberParts', data);
        return { prefix: '', number: formatPercentNumber(data, notation) ?? '', suffix: '' };
    }
}
